/**
 * ScriptTrackMockup — purely visual mock of the ScriptTrack Compliance
 * Dashboard panel.  No live data; used on the DSCSA landing page hero to
 * give visitors a tangible preview of what they get when they sign up.
 */
export default function ScriptTrackMockup() {
  return (
    <div className="relative w-full max-w-sm mx-auto">
      {/* Ambient glow */}
      <div
        className="absolute inset-0 rounded-2xl blur-2xl opacity-30"
        style={{ background: "radial-gradient(ellipse at 60% 40%, #10b981 0%, #054FF0 60%, transparent 100%)" }}
        aria-hidden="true"
      />

      {/* Card */}
      <div className="relative bg-white rounded-2xl border border-gray-200 shadow-2xl overflow-hidden">
        {/* Top bar */}
        <div className="bg-primary px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-red-400" />
            <div className="w-2 h-2 rounded-full bg-yellow-400" />
            <div className="w-2 h-2 rounded-full bg-green-400" />
          </div>
          <span className="text-white/80 text-xs font-medium">ScriptTrack™ Compliance Dashboard</span>
          <div className="w-12" />
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* Illustrative product preview — not a compliance outcome for a
              fictional pharmacy. */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400 font-medium uppercase tracking-widest mb-0.5">Compliance Status</p>
              <p className="text-primary font-bold text-sm">Your Pharmacy</p>
            </div>
            <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-1.5 rounded-full">
              <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-xs font-bold">Records organized</span>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Stat rows */}
          <div className="space-y-3">
            {[
              {
                icon: (
                  <svg className="w-4 h-4 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ),
                label: "Transaction Records",
                value: "Example",
                sub: "TI + TS workspace",
              },
              {
                icon: (
                  <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                ),
                label: "Receiving Workflow",
                value: "Review",
                sub: "Product-identifier checks",
              },
              {
                icon: (
                  <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                label: "Team Review",
                value: "Ready",
                sub: "Organize next steps",
              },
            ].map((row) => (
              <div key={row.label} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center flex-shrink-0">
                  {row.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-500 truncate">{row.label}</span>
                    <span className="text-sm font-bold text-primary flex-shrink-0">{row.value}</span>
                  </div>
                  <div className="mt-0.5 h-1 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-400 rounded-full" style={{ width: row.value === "Example" ? "72%" : row.value === "Review" ? "54%" : "88%" }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{row.sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100" />

          {/* Bottom CTA row */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">Next team review in 24h</p>
            <span className="inline-flex items-center gap-1 text-accent text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
              Live
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
